import { AgGridAngular } from "@ag-grid-community/angular";
import { GridOptions, ICellRendererParams } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject, signal } from "@angular/core";
import { AccountMarketplace } from "@front/m19-api-client";
import { gridConfig } from "@front/m19-grid-config";
import { CatalogBrandEx } from "@front/m19-models";
import { AccountSelectionService, CatalogBrandService } from "@front/m19-services";
import {
  ActionButton,
  ActionButtonsComponent,
  IGridButtonComponent,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoService } from "@jsverse/transloco";
import { GridLayoutComponent } from "@m19-board/containers/grid-layout/grid-layout.component";
import { actionColumnProperties } from "@m19-board/grid-config/grid-columns";
import { exportGridCsv, getCsvFileName } from "@m19-board/grid-config/grid-config";
import { InputModalComponent, InputModalData } from "@m19-board/shared/input-modal/input-modal.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { switchMap, take, tap } from "rxjs";
import { BrandAliasModalComponent, BrandAliasModalData } from "./brand-alias-modal.component";
import { BrandAsinsModalComponent, BrandAsinsModalData } from "./brand-asins-modal.component";
import { BrandMergeModalComponent, BrandMergeModalData } from "./brand-merge-modal.component";

@UntilDestroy()
@Component({
  selector: "app-brand-alias",
  templateUrl: "./brand-alias.component.html",
  imports: [AgGridAngular, GridLayoutComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandAliasComponent implements OnInit {
  private accountSelectionService = inject(AccountSelectionService);
  private catalogBrandService = inject(CatalogBrandService);
  private modalService = inject(ModalService);
  private newModalService = inject(ModalService);
  private toastrService = inject(ToasterService);
  private translocoService = inject(TranslocoService);

  loading = true;
  brandAliases = signal<CatalogBrandEx[]>([]);
  accountMarketplace!: AccountMarketplace;
  isReadOnly!: boolean;

  @ViewChild(AgGridAngular) brandGrid!: AgGridAngular;

  readonly editIconProperties = {
    color: "gray",
    variant: "ghost",
    square: true,
    icon: "edit",
    iconOnHover: true,
    trailing: true,
  };

  readonly gridOptions: GridOptions<CatalogBrandEx> = {
    ...gridConfig({ gridKey: "catalogBrand" }),
    sideBar: false,
    defaultColDef: {
      flex: 1,
      resizable: true,
      suppressMovable: true,
      menuTabs: ["filterMenuTab"],
    },
    getRowStyle: (params) => {
      if (params.node.group || params.node.parent?.allChildrenCount == 1) {
        return { "background-color": "white !important" };
      }
      return undefined;
    },
    suppressRowHoverHighlight: true,
    groupRemoveSingleChildren: true,
    groupDefaultExpanded: -1,
    groupDisplayType: "custom",
    columnDefs: [
      {
        colId: "name",
        headerValueGetter: () => this.translocoService.translate("page-title.brand_alias"),
        headerName: this.translocoService.translate("page-title.brand_alias", {}, "en"),
        showRowGroup: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        cellClass: "sensitive-data",
        cellRendererSelector: (params) => {
          return {
            component: "agGroupCellRenderer",
            params: {
              innerRendererSelector: (params: ICellRendererParams) => {
                if (
                  !params.node.group &&
                  params.node.parent?.allChildrenCount &&
                  params.node.parent.allChildrenCount > 1
                ) {
                  return undefined;
                }
                const brandId = params.node.group
                  ? params.node.allLeafChildren[0].data.parentBrandId
                  : params.data.brandId;
                return {
                  component: IGridButtonComponent,
                  params: {
                    label: params.value,
                    tooltipValue: this.translocoService.translate("brand-alias.edit_brand_name"),
                    ...this.editIconProperties,
                    disabled: this.isReadOnly,
                    clickAction: () => {
                      const ref = this.newModalService.openModal<InputModalData, string>(InputModalComponent, {
                        modalTitle: this.translocoService.translate("brand-alias.change_name_title"),
                        data: {
                          initialValue: params.value,
                          maxLength: 80,
                        },
                      });
                      ref
                        .afterClosed()
                        .pipe(
                          take(1),
                          switchMap((newName: string) => {
                            return this.catalogBrandService.setBrandAlias(
                              this.accountMarketplace.accountId,
                              this.accountMarketplace.marketplace,
                              brandId,
                              newName,
                            );
                          }),
                        )
                        .subscribe({
                          next: () => {
                            this.toastrService.success(
                              this.translocoService.translate("brand-alias.update_name_success"),
                            );
                          },
                          error: (e) => {
                            this.toastrService.error(
                              e,
                              this.translocoService.translate("brand-alias.update_name_error"),
                            );
                          },
                        });
                    },
                  },
                };
              },
            },
          };
        },
      },
      {
        headerValueGetter: () => this.translocoService.translate("brand-alias.parent_brand"),
        headerName: this.translocoService.translate("brand-alias.parent_brand", {}, "en"),
        field: "parentBrandAlias",
        rowGroup: true,
        hide: true,
      },
      {
        headerValueGetter: () => this.translocoService.translate("brand-alias.brand_from_catalog"),
        headerName: this.translocoService.translate("brand-alias.brand_from_catalog", {}, "en"),
        field: "brandName",
        filter: "agTextColumnFilter",
        floatingFilter: true,
        cellClass: "sensitive-data",
      },
      {
        headerValueGetter: () => this.translocoService.translate("common.asins"),
        headerName: this.translocoService.translate("common.asins"),
        field: "brandProducts",
        sortable: false,
        cellRendererSelector: (params) => {
          if (params.node.group) {
            return undefined;
          }
          return {
            component: IGridButtonComponent,
            params: {
              variant: "ghost",
              iconOnHover: true,
              label: this.translocoService.translate("common.view"),
              size: "sm",
              clickAction: () => {
                this.modalService.openModal<BrandAsinsModalData, void>(BrandAsinsModalComponent, {
                  size: "lg",
                  height: "fill",
                  data: {
                    accountId: this.accountMarketplace.accountId,
                    marketplace: this.accountMarketplace.marketplace,
                    brandName: params.data?.brandName ?? "",
                  },
                });
              },
            },
          };
        },
      },
      {
        colId: "otherNames",
        headerValueGetter: () => this.translocoService.translate("brand-alias.other_brand_names"),
        headerName: this.translocoService.translate("brand-alias.other_brand_names", {}, "en"),
        valueGetter: (params) => {
          if (params.node?.group) {
            return undefined;
          }
          return params.data?.extraSegments?.length;
        },
        cellRendererSelector: (params) => {
          if (params.node.group) {
            return undefined;
          }
          const label =
            params.value == 0 ? "" : this.translocoService.translate("brand-alias.other_name", { count: params.value });
          return {
            component: IGridButtonComponent,
            params: {
              tooltipValue: this.translocoService.translate("brand-alias.edit_other_brand"),
              ...this.editIconProperties,
              iconOnHover: false,
              label,
              clickAction: () => {
                this.modalService.openModal<BrandAliasModalData, void>(BrandAliasModalComponent, {
                  size: "lg",
                  modalTitle: `${this.translocoService.translate("common.edit")} "${params.data?.brandAlias}" ${this.translocoService.translate("brand-alias-modal.other_brand_name")}`,
                  data: {
                    catalogBrand: params.data!,
                    accountId: this.accountMarketplace.accountId,
                    marketplace: this.accountMarketplace.marketplace,
                    isReadOnly: this.isReadOnly,
                  },
                });
              },
            },
          };
        },
      },
      {
        ...actionColumnProperties<CatalogBrandEx, string>(),
        headerValueGetter: () => this.translocoService.translate("brand-alias.actions"),
        headerName: this.translocoService.translate("brand-alias.actions", {}, "en"),
        cellRendererSelector: (params) => {
          const actionButtons: ActionButton[] = [];
          if (!params.data) {
            return undefined;
          }
          if (this.isReadOnly || this.brandAliases().length <= 1) {
            return undefined;
          }
          if (params.data.parentBrandId == undefined) {
            actionButtons.push({
              icon: "link",
              tooltip: this.translocoService.translate("brand-alias.merge_with_other_brand"),
              onClick: () => {
                const modalRef = this.modalService.openModal<BrandMergeModalData, CatalogBrandEx>(
                  BrandMergeModalComponent,
                  {
                    size: "sm",
                    data: {
                      catalogBrand: params.data!,
                    },
                  },
                );

                modalRef
                  .afterClosed()
                  .pipe(
                    untilDestroyed(this),
                    take(1),
                    switchMap((selectedBrand) => {
                      return this.catalogBrandService.mergeBrands(
                        this.accountMarketplace.accountId,
                        this.accountMarketplace.marketplace,
                        params.data!.brandId!,
                        selectedBrand.brandId!,
                      );
                    }),
                  )
                  .subscribe({
                    next: () => {
                      this.toastrService.success(this.translocoService.translate("brand-alias.brand_merge_success"));
                      this.brandGrid.api?.refreshCells();
                    },
                    error: (e) => {
                      this.toastrService.error(e, this.translocoService.translate("brand-alias.brand_merge_error"));
                    },
                  });
              },
            });
          } else if (params.data.parentBrandId !== params.data.brandId) {
            actionButtons.push({
              icon: "linkVariantOff",
              tooltip: this.translocoService.translate("brand-alias.unmerge"),
              onClick: () => {
                this.catalogBrandService
                  .unmergeBrands(
                    this.accountMarketplace.accountId,
                    this.accountMarketplace.marketplace,
                    params.data!.brandId!,
                  )
                  .subscribe({
                    next: () => {
                      this.toastrService.success(this.translocoService.translate("brand-alias.brand_unmerge_success"));
                    },
                    error: (e) => {
                      this.toastrService.error(e, this.translocoService.translate("brand-alias.brand_unmerge_error"));
                    },
                  });
              },
            });
          }
          return {
            component: ActionButtonsComponent,
            params: {
              actionButtons,
            },
          };
        },
      },
    ],
  };

  constructor() {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(untilDestroyed(this))
      .subscribe((accountMarketplace) => {
        this.accountMarketplace = accountMarketplace;
      });
    this.accountSelectionService.readOnlyMode$.pipe(untilDestroyed(this)).subscribe((readOnly) => {
      this.isReadOnly = readOnly;
    });
  }

  ngOnInit(): void {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        tap(() => (this.loading = true)),
        switchMap((accountMarketplace: AccountMarketplace) => {
          return this.catalogBrandService.getCatalogBrands(
            accountMarketplace.accountId,
            accountMarketplace.marketplace,
          );
        }),
      )
      .subscribe((brandAliases) => {
        this.brandAliases.set(brandAliases);
        this.brandGrid?.api?.updateGridOptions({ rowData: brandAliases });
        this.loading = false;
      });
  }

  exportAsCsv() {
    const fileName = getCsvFileName(
      "brand_alias",
      this.accountMarketplace.accountGroupName,
      this.accountMarketplace.marketplace,
    );
    exportGridCsv(this.brandGrid.api, {
      fileName,
      skipRowGroups: true,
      columnKeys: ["name", "brandName", "brandProducts", "otherNames"],
    });
  }
}
